import { openFileInCurrentWindow } from 'web/src/utilities/download-file';
import {
  FileResult,
  GetRecipeCostRequest,
  RecipeCostHeader,
  USDAProductStatusTypeData,
  UserSettingPreference,
} from '../../api';
import {
  CreateRecipeCategoryRequest,
  DeleteRecipeCategoryRequest,
  RecipeCategory,
  UpdateRecipeCategoryRequest,
} from '../../api/models/recipe-category.models';
import { RecipeCustomerShare, ShareRecipeCustomersRequest } from '../../api/models/recipe-customer-share.models';
import { UpdateRecipeCustomerUserManagesRequest } from '../../api/models/recipe-customer-user-manage.models';
import {
  CreateRecipeDetailRequest,
  DeleteRecipeDetailRequest,
  RecipeDetail,
  SequenceRecipeDetailsRequest,
  UpdateRecipeDetailRequest,
} from '../../api/models/recipe-detail.models';
import {
  RecipeHeaderPrivateUser,
  UpdateRecipeHeaderPrivateUsersRequest,
} from '../../api/models/recipe-header-private-user.models';
import {
  CopyRecipeHeaderRequest,
  CreateRecipeHeaderRequest,
  DeleteRecipeHeaderRequest,
  RecipeHeader,
  UpdateHiddenSecretRecipeHeaderRequest,
  UpdateRecipeHeaderRequest,
} from '../../api/models/recipe-header.models';
import {
  RecipeCostingOptionDefaults,
  RecipeCostingRequest,
  RecipeDetailOptionDefaults,
  RecipeDetailRequest,
  RecipeMenuCostingOptionDefaults,
  RecipeMenuCostingRequest,
} from '../../api/models/recipe-report.models';
import RecipeCategoryService from '../../api/services/recipe-category.service';
import RecipeCostService from '../../api/services/recipe-cost.service';
import RecipeCustomerDiaryService from '../../api/services/recipe-customer-diary.service';
import RecipeCustomerShareService from '../../api/services/recipe-customer-share.service';
import RecipeCustomerUserManageService from '../../api/services/recipe-customer-user-manage.service';
import RecipeDetailService from '../../api/services/recipe-detail.service';
import RecipeDiaryService from '../../api/services/recipe-diary.service';
import RecipeHeaderPrivateUserService from '../../api/services/recipe-header-private-user.service';
import RecipeHeaderService from '../../api/services/recipe-header.service';
import RecipeReportService from '../../api/services/recipe-report.service';
import RecipeUomService from '../../api/services/recipe-uom.service';
import RecipeUsersService from '../../api/services/recipe-users.service';
import { addDays, generateStaticId, truncateTime } from '../../helpers';
import { useAppInsightsLogger } from '../../logging/AppInsightsLogger';
import {
  NotificationDisplayType,
  NotificationKeys,
  NotificationType,
  Resolution,
  UserActivityAction,
  UserActivityActionSummary,
  UserActivityPageName,
} from '../../models';
import {
  globalSlice,
  logUserActivity,
  updateCurrentUserSetting,
  upsertAppNotification,
  upsertAppNotificationByKey,
} from '../common';
import { AppDispatch, AppThunk, RootState } from '../store';
import {
  recipesSlice,
  selectAllRecipeCustomerShares,
  selectAllRecipeCustomerUserManages,
  selectAllRecipeHeaderById,
  selectAllRecipeHeaderPrivateUsers,
  selectAllRecipeHeaderPrivateUsersData,
  selectRecipeDetailUsdaProducts,
} from './recipes.slice';
import { getUSDAProductStatusTypes } from '../orders';

const appInsightsLogger = useAppInsightsLogger();
const recipeHeaderService = RecipeHeaderService.getInstance();
const recipeCategoryService = RecipeCategoryService.getInstance();
const recipeDetailService = RecipeDetailService.getInstance();
const recipeCustomerDiaryService = RecipeCustomerDiaryService.getInstance();
const recipeDiaryService = RecipeDiaryService.getInstance();
const recipeUomService = RecipeUomService.getInstance();
const recipeCustomerShareService = RecipeCustomerShareService.getInstance();
const recipeHeaderPrivateUserService = RecipeHeaderPrivateUserService.getInstance();
const recipeCustomerUserManageService = RecipeCustomerUserManageService.getInstance();
const recipeCostService = RecipeCostService.getInstance();
const recipeUsersService = RecipeUsersService.getInstance();
const recipeReportService = RecipeReportService.getInstance();

/*
 * Recipe Category
 */

export const getRecipeCategorys =
  (customerId: string): AppThunk =>
  async (dispatch, getState) => {
    try {
      if (!getState().recipes.isRecipesCategoriesLoading)
        dispatch(recipesSlice.actions.setIsLoadingRecipeCategories(true));
      const { data } = await recipeCategoryService.getRecipeCategorys(customerId);
      if (data.IsSuccess) {
        dispatch(recipesSlice.actions.setRecipeCategories(data.ResultObject));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      dispatch(recipesSlice.actions.setIsLoadingRecipeCategories(false));
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
    }
  };

/*
 * Recipe Header
 */

export const getRecipeHeader =
  (recipeHeaderId: string, customerId: string): AppThunk =>
  async (dispatch, getState) => {
    try {
      if (selectAllRecipeHeaderById(getState().recipes.recipeHeaders, recipeHeaderId)) {
        return; // no-op record persists in redux
      }
      if (!getState().recipes.isRecipeHeaderLoading) dispatch(recipesSlice.actions.setIsLoadingRecipeHeader(true));
      const { data } = await recipeHeaderService.getRecipeHeader(recipeHeaderId, customerId);
      if (data.IsSuccess) {
        dispatch(recipesSlice.actions.setRecipeHeader(data.ResultObject));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      dispatch(recipesSlice.actions.setIsLoadingRecipeHeader(false));
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
    }
  };

export const getRecipeHeaders =
  (
    customerId: string,
    menuHeaderId?: string,
    onSuccess?: (data: RecipeHeader[]) => void,
    isPreload?: boolean
  ): AppThunk =>
  async (dispatch, getState) => {
    try {
      if (
        getState().recipes.gridParameters?.customerId === customerId &&
        getState().recipes.gridParameters?.menuHeaderId === menuHeaderId
      ) {
        return; // no-op
      }

      if (!isPreload) {
        dispatch(recipesSlice.actions.setGridParameters({ customerId, menuHeaderId }));
      }

      if (!getState().recipes.isRecipeHeadersLoading) dispatch(recipesSlice.actions.setIsLoadingRecipeHeaders(true));
      const { data } = await recipeHeaderService.getRecipeHeaders(customerId, menuHeaderId);
      if (data.IsSuccess) {
        dispatch(recipesSlice.actions.setRecipeHeaders(data.ResultObject));
        onSuccess?.(data.ResultObject);
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      dispatch(recipesSlice.actions.setIsLoadingRecipeHeaders(false));
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
    }
  };

export const createRecipeHeader =
  (request: CreateRecipeHeaderRequest, resolution: Resolution): AppThunk<Promise<RecipeHeader | undefined>> =>
  async (dispatch): Promise<RecipeHeader | undefined> => {
    let result;
    try {
      const { data } = await recipeHeaderService.createRecipeHeader(request);
      if (data.IsSuccess) {
        dispatch(updateCurrentUserSetting({ ShowRecipeCosting: false } as UserSettingPreference, { optimistic: true }));
        dispatch(recipesSlice.actions.setRecipeHeader(data.ResultObject));
        dispatch(recipesSlice.actions.resetGridParameters());
        dispatch(
          logUserActivity({
            action: UserActivityAction.CreateRecipe,
            pageName: UserActivityPageName.Recipes,
            actionSummary: UserActivityActionSummary.CreateRecipe,
            resolution: resolution,
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      result = data?.ResultObject;
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
    }
    return result;
  };

export const updateRecipeHeader =
  (request: UpdateRecipeHeaderRequest, resolution: Resolution): AppThunk<Promise<RecipeHeader | undefined>> =>
  async (dispatch, getState): Promise<RecipeHeader | undefined> => {
    let result;
    try {
      const { data } = await recipeHeaderService.updateRecipeHeader(request);
      if (data.IsSuccess) {
        dispatch(recipesSlice.actions.setRecipeHeader(data.ResultObject));
        dispatch(recipesSlice.actions.resetGridParameters());
        dispatch(
          logUserActivity({
            action: UserActivityAction.EditRecipe,
            pageName: UserActivityPageName.Recipes,
            actionSummary: UserActivityActionSummary.EditRecipe,
            resolution: resolution,
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      result = data?.ResultObject;
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
    }
    return result;
  };

export const updateHiddenRecipeHeaders =
  (
    request: UpdateHiddenSecretRecipeHeaderRequest[],
    customerId: string,
    resolution: Resolution
  ): AppThunk<Promise<RecipeHeader[] | undefined>> =>
  async (dispatch, getState): Promise<RecipeHeader[] | undefined> => {
    let result;
    try {
      if (!getState().recipes.isRecipeHeadersLoading) dispatch(recipesSlice.actions.setIsLoadingRecipeHeaders(true));

      const { data } = await recipeHeaderService.updateHiddenRecipeHeader({
        RecipeUpdates: request,
        ViewAsCustomerId: customerId,
      });

      if (data.IsSuccess) {
        // const updates = data.ResultObject.map((r) => ({
        //   recipeHeaderId: r.RecipeHeaderId,
        //   isHidden: r.IsHidden,
        // }));
        //dispatch(recipesSlice.actions.setRecipeHeadersIsHidden(updates));
        dispatch(recipesSlice.actions.resetGridParameters());
        dispatch(getRecipeHeaders(customerId));
        dispatch(
          logUserActivity({
            action: UserActivityAction.UpdateHiddenRecipe,
            pageName: UserActivityPageName.ManageRecipeAccess,
            actionSummary: UserActivityActionSummary.UpdateHiddenRecipe,
            resolution,
          })
        );
      } else {
        dispatch(recipesSlice.actions.setIsLoadingRecipeHeaders(false));
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      result = data?.ResultObject;
    } catch (error) {
      dispatch(recipesSlice.actions.setIsLoadingRecipeHeaders(false));
      appInsightsLogger.trackException({ exception: error });
    }
    return result;
  };

export const updateSecretRecipeHeaders =
  (
    request: UpdateHiddenSecretRecipeHeaderRequest[],
    customerId: string,
    resolution: Resolution
  ): AppThunk<Promise<RecipeHeader[] | undefined>> =>
  async (dispatch, getState): Promise<RecipeHeader[] | undefined> => {
    let result;
    try {
      if (!getState().recipes.isRecipeHeadersLoading) dispatch(recipesSlice.actions.setIsLoadingRecipeHeaders(true));

      const { data } = await recipeHeaderService.updateSecretRecipeHeader({
        RecipeUpdates: request,
        ViewAsCustomerId: customerId,
      });

      if (data.IsSuccess) {
        // const updates = data.ResultObject.map((r) => ({
        //   recipeHeaderId: r.RecipeHeaderId,
        //   isPrivate: r.IsPrivate,
        // }));
        //dispatch(recipesSlice.actions.setRecipeHeadersIsPrivate(updates));
        dispatch(recipesSlice.actions.resetGridParameters());
        dispatch(getRecipeHeaders(customerId));
        dispatch(getRecipeHeaderPrivateUsersByCustomer(customerId, true));
        dispatch(
          logUserActivity({
            action: UserActivityAction.UpdateSecretRecipe,
            pageName: UserActivityPageName.ManageRecipeAccess,
            actionSummary: UserActivityActionSummary.UpdateSecretRecipe,
            resolution,
          })
        );
      } else {
        dispatch(recipesSlice.actions.setIsLoadingRecipeHeaders(false));
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      result = data?.ResultObject;
    } catch (error) {
      dispatch(recipesSlice.actions.setIsLoadingRecipeHeaders(false));
      appInsightsLogger.trackException({ exception: error });
    }
    return result;
  };

export const deleteRecipeHeader =
  (request: DeleteRecipeHeaderRequest, resolution: Resolution): AppThunk<Promise<boolean>> =>
  async (dispatch, getState): Promise<boolean> => {
    let result = false;
    try {
      const { data } = await recipeHeaderService.deleteRecipeHeader(request);
      if (data.IsSuccess) {
        result = true;
        dispatch(recipesSlice.actions.removeRecipeHeader(request.RecipeHeaderId));
        dispatch(recipesSlice.actions.resetGridParameters());
        dispatch(
          logUserActivity({
            action: UserActivityAction.DeleteRecipe,
            pageName: UserActivityPageName.Recipes,
            actionSummary: UserActivityActionSummary.DeleteRecipe,
            resolution: resolution,
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
    }
    return result;
  };

export const copyRecipeHeader =
  (request: CopyRecipeHeaderRequest, resolution: Resolution): AppThunk<Promise<RecipeHeader | undefined>> =>
  async (dispatch) => {
    let result;
    try {
      const { data } = await recipeHeaderService.copyRecipeHeader(request);
      if (data.IsSuccess) {
        dispatch(recipesSlice.actions.setRecipeHeader(data.ResultObject));
        dispatch(recipesSlice.actions.resetGridParameters());
        dispatch(
          logUserActivity({
            action: UserActivityAction.CopyRecipe,
            pageName: UserActivityPageName.Recipes,
            actionSummary: UserActivityActionSummary.CopyRecipe,
            resolution: resolution,
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      result = data?.ResultObject;
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
    }
    return result;
  };

/*
 * Recipe Categories
 */

export const getRecipeCategories =
  (customerId: string): AppThunk =>
  async (dispatch, getState) => {
    try {
      if (!getState().recipes.isRecipesCategoriesLoading)
        dispatch(recipesSlice.actions.setIsLoadingRecipeCategories(true));
      const { data } = await recipeCategoryService.getRecipeCategorys(customerId);
      if (data.IsSuccess) {
        dispatch(recipesSlice.actions.setRecipeCategories(data.ResultObject));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      dispatch(recipesSlice.actions.setIsLoadingRecipeCategories(false));
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
    }
  };

export const createRecipeCategory =
  (request: CreateRecipeCategoryRequest, resolution: Resolution): AppThunk<Promise<RecipeCategory | undefined>> =>
  async (dispatch) => {
    let result;

    try {
      const { data } = await recipeCategoryService.createRecipeCategory(request);
      if (data.IsSuccess) {
        dispatch(upsertAppNotificationByKey(NotificationKeys.Toast, '', 'Category has been created'));
        dispatch(recipesSlice.actions.setRecipeCategory(data.ResultObject));
        dispatch(recipesSlice.actions.resetGridParameters());
        dispatch(
          logUserActivity({
            action: UserActivityAction.CreateRecipeCategory,
            pageName: UserActivityPageName.RecipeCategories,
            actionSummary: UserActivityActionSummary.CreateRecipeCategory,
            resolution: resolution,
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      result = data?.ResultObject;
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
    }
    return result;
  };

export const updateRecipeCategory =
  (request: UpdateRecipeCategoryRequest, resolution: Resolution): AppThunk<Promise<RecipeCategory | undefined>> =>
  async (dispatch) => {
    let result;
    try {
      const { data } = await recipeCategoryService.updateRecipeCategory(request);
      if (data.IsSuccess) {
        dispatch(upsertAppNotificationByKey(NotificationKeys.Toast, '', 'Category has been updated'));
        dispatch(recipesSlice.actions.setRecipeCategory(data.ResultObject));
        dispatch(recipesSlice.actions.resetGridParameters());
        dispatch(
          logUserActivity({
            action: UserActivityAction.EditRecipeCategory,
            pageName: UserActivityPageName.RecipeCategories,
            actionSummary: UserActivityActionSummary.EditRecipeCategory,
            resolution: resolution,
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      result = data?.ResultObject;
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
    }
    return result;
  };

export const deleteRecipeCategory =
  (request: DeleteRecipeCategoryRequest, resolution: Resolution): AppThunk<Promise<void>> =>
  async (dispatch) => {
    try {
      const { data } = await recipeCategoryService.deleteRecipeCategory(request);
      if (data.IsSuccess) {
        dispatch(upsertAppNotificationByKey(NotificationKeys.Toast, '', 'Category has been deleted'));
        dispatch(recipesSlice.actions.removeRecipeCategory(request.RecipeCategoryId));
        dispatch(recipesSlice.actions.resetGridParameters());
        dispatch(
          logUserActivity({
            action: UserActivityAction.DeleteRecipeCategory,
            pageName: UserActivityPageName.RecipeCategories,
            actionSummary: UserActivityActionSummary.DeleteRecipeCategory,
            resolution: resolution,
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
    }
  };

/*
 * Recipe Details
 */

export const getRecipeDetails =
  (
    recipeHeaderId: string,
    customerId: string,
    servingsScaled?: number
  ): AppThunk<Promise<RecipeDetail[] | undefined>> =>
  async (dispatch, getState) => {
    let result = undefined;
    if (!getState().recipes.isRecipeDetailsLoading) dispatch(recipesSlice.actions.setIsLoadingRecipeDetails(true));
    try {
      const { data } = await recipeDetailService.getRecipeDetails(recipeHeaderId, customerId, servingsScaled);
      if (data.IsSuccess) {
        dispatch(recipesSlice.actions.setRecipeDetails(data.ResultObject));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      result = data.ResultObject;
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
    }
    dispatch(recipesSlice.actions.setIsLoadingRecipeDetails(false));
    return result;
  };

export const createRecipeDetail =
  (request: CreateRecipeDetailRequest, resolution: Resolution): AppThunk<Promise<RecipeDetail | undefined>> =>
  async (dispatch) => {
    let result;
    try {
      const { data } = await recipeDetailService.createRecipeDetail(request);
      if (data.IsSuccess) {
        dispatch(recipesSlice.actions.setRecipeDetail(data.ResultObject));
        dispatch(recipesSlice.actions.resetGridParameters());
        dispatch(
          logUserActivity({
            action: UserActivityAction.EditRecipe,
            pageName: UserActivityPageName.RecipeDetail,
            actionSummary: UserActivityActionSummary.EditRecipe,
            resolution: resolution,
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      result = data?.ResultObject;
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
    }
    return result;
  };

export const deleteRecipeDetail =
  (request: DeleteRecipeDetailRequest, resolution: Resolution): AppThunk<Promise<void>> =>
  async (dispatch) => {
    try {
      const { data } = await recipeDetailService.deleteRecipeDetail(request);
      if (data.IsSuccess) {
        dispatch(recipesSlice.actions.removeRecipeDetail(request.RecipeDetailId));
        dispatch(recipesSlice.actions.resetGridParameters());
        dispatch(
          logUserActivity({
            action: UserActivityAction.EditRecipe,
            pageName: UserActivityPageName.RecipeDetail,
            actionSummary: UserActivityActionSummary.EditRecipe,
            resolution: resolution,
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
    }
  };

export const updateRecipeDetail =
  (request: UpdateRecipeDetailRequest, resolution: Resolution): AppThunk<Promise<RecipeDetail | undefined>> =>
  async (dispatch) => {
    let result;
    try {
      const { data } = await recipeDetailService.updateRecipeDetail(request);
      if (data.IsSuccess) {
        dispatch(recipesSlice.actions.setRecipeDetail(data.ResultObject));
        dispatch(recipesSlice.actions.resetGridParameters());
        dispatch(
          logUserActivity({
            action: UserActivityAction.EditRecipe,
            pageName: UserActivityPageName.RecipeDetail,
            actionSummary: UserActivityActionSummary.EditRecipe,
            resolution: resolution,
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      result = data?.ResultObject;
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
    }
    return result;
  };

// Recipe customer diaries
export const getRecipeCustomerDiaries =
  (customerId: string, resolution: Resolution, startDate?: Date | string, endDate?: Date | string): AppThunk =>
  async (dispatch, getState) => {
    try {
      if (!getState().recipes.isRecipeCustomerDiariesLoading) {
        dispatch(recipesSlice.actions.setIsRecipeCustomerDiariesLoading(true));
      }

      if (!endDate) {
        endDate = truncateTime(new Date());
        endDate = addDays(endDate, 1);
      }
      if (!startDate) {
        startDate = truncateTime(new Date());
        startDate = truncateTime(addDays(startDate, -7));
      }

      const { data } = await recipeCustomerDiaryService.getRecipeCustomerDiaries(customerId, startDate, endDate);

      if (data.IsSuccess) {
        dispatch(recipesSlice.actions.setRecipeCustomerDiaries(data.ResultObject));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }

      dispatch(recipesSlice.actions.setIsRecipeCustomerDiariesLoading(false));
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
  };

// Recipe diaries
export const getRecipeDiaries =
  (
    customerId: string,
    resolution: Resolution,
    recipeHeaderId?: string,
    startDate?: Date | string,
    endDate?: Date | string
  ): AppThunk =>
  async (dispatch, getState) => {
    try {
      if (!getState().recipes.isRecipeDiariesLoading) {
        dispatch(recipesSlice.actions.setIsRecipeDiariesLoading(true));
      }

      if (!endDate) {
        endDate = truncateTime(new Date());
        endDate = addDays(endDate, 1);
      }
      if (!startDate) {
        startDate = truncateTime(new Date());
        startDate = truncateTime(addDays(startDate, -7));
      }

      const { data } = await recipeDiaryService.getRecipeDiarys({
        CustomerId: customerId,
        RecipeHeaderId: recipeHeaderId,
        StartDate: startDate,
        EndDate: endDate,
      });

      if (data.IsSuccess) {
        dispatch(recipesSlice.actions.setRecipeDiaries(data.ResultObject));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }

      dispatch(recipesSlice.actions.setIsRecipeDiariesLoading(false));
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
  };

export const revertRecipeCustomerDiaryAction =
  (recipeDiaryId: string, resolution: Resolution): AppThunk =>
  async (dispatch) => {
    try {
      const { data } = await recipeCustomerDiaryService.revertRecipeCustomerDiaryAction({
        RecipeCustomerDiaryId: recipeDiaryId,
      });

      if (data.IsSuccess) {
        dispatch(recipesSlice.actions.removeRecipeCustomerDiary(recipeDiaryId));
        dispatch(recipesSlice.actions.resetGridParameters());
        dispatch(
          logUserActivity({
            action: UserActivityAction.RevertDeletedRecipe,
            pageName: UserActivityPageName.Recipes,
            actionSummary: UserActivityActionSummary.RevertDeletedRecipe,
            resolution: resolution,
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
  };

// Recipe UOMs
export const getRecipeUoms = (): AppThunk => async (dispatch, getState) => {
  try {
    if (getState().recipes.recipeUoms.ids.length > 0) {
      return;
    }

    if (!getState().recipes.isRecipeUomsLoading) {
      dispatch(recipesSlice.actions.setIsRecipeUomsLoading(true));
    }

    const { data } = await recipeUomService.getRecipeUnitOfMeasures();

    if (data.IsSuccess) {
      dispatch(recipesSlice.actions.setRecipeUoms(data.ResultObject));
    } else {
      dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
    }

    dispatch(recipesSlice.actions.setIsRecipeUomsLoading(false));
  } catch (exception) {
    appInsightsLogger.trackException({ exception });
  }
};

/*
 * Recipe Customer Share
 */

export const getRecipeCustomerShares =
  (
    customerId: string,
    callback?: (shares: RecipeCustomerShare[]) => void | Promise<void>,
    refresh?: boolean
  ): AppThunk =>
  async (dispatch, getState) => {
    try {
      const items = selectAllRecipeCustomerShares(getState().recipes.recipeCustomerShares);
      const item = items?.[0];
      if (!refresh && item?.CustomerId === customerId) {
        callback?.(items);
        return;
      }

      if (!customerId) return;

      if (!getState().recipes.isRecipeCustomerSharesLoading)
        dispatch(recipesSlice.actions.setIsLoadingRecipeCustomerShares(true));

      const { data } = await recipeCustomerShareService.getRecipeCustomerShares(customerId);
      if (data.IsSuccess) {
        dispatch(recipesSlice.actions.setRecipeCustomerShares(data.ResultObject));
        callback?.(data.ResultObject);
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      dispatch(recipesSlice.actions.setIsLoadingRecipeCustomerShares(false));
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
    }
  };

export const shareRecipeCustomers =
  (request: ShareRecipeCustomersRequest, resolution: Resolution, onSuccess?: () => void): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    try {
      if (!getState().recipes.isRecipeCustomerSharesLoading)
        dispatch(recipesSlice.actions.setIsLoadingRecipeCustomerShares(true));

      const { data } = await recipeCustomerShareService.shareRecipeCustomers(request);
      if (data.IsSuccess) {
        dispatch(
          logUserActivity({
            action: UserActivityAction.ShareRecipe,
            pageName: UserActivityPageName.ManageRecipeAccess,
            actionSummary: UserActivityActionSummary.ShareRecipe,
            resolution: resolution,
          })
        );
        dispatch(getRecipeCustomerShares(request.CustomerId, undefined, true));
        onSuccess?.();
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
        dispatch(recipesSlice.actions.setIsLoadingRecipeCustomerShares(false));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
      dispatch(recipesSlice.actions.setIsLoadingRecipeCustomerShares(false));
    }
  };

// Recipe header private users
export const getRecipeHeaderPrivateUsersByCustomer =
  (viewAsCustomerId: string, refresh?: boolean): AppThunk<Promise<RecipeHeaderPrivateUser[]>> =>
  async (dispatch, getState) => {
    try {
      const items = selectAllRecipeHeaderPrivateUsers(getState().recipes.recipeHeaderPrivateUsers);
      if (!refresh && items.length > 0) {
        return items;
      }

      if (!getState().recipes.isRecipeHeaderPrivateUsersLoading) {
        dispatch(recipesSlice.actions.setIsRecipeHeaderPrivateUsersLoading(true));
      }

      const { data } = await recipeHeaderPrivateUserService.getRecipeHeaderPrivateUsersByCustomer(viewAsCustomerId);

      if (data.IsSuccess) {
        dispatch(recipesSlice.actions.setRecipeHeaderPrivateUsers(data.ResultObject));
        return data.ResultObject;
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
        return [];
      }
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
      return [];
    } finally {
      dispatch(recipesSlice.actions.setIsRecipeHeaderPrivateUsersLoading(false));
    }
  };

export const getRecipeHeaderPrivateUsers =
  (recipeHeaderId: string, viewAsCustomerId: string, refresh?: boolean): AppThunk<Promise<RecipeHeaderPrivateUser[]>> =>
  async (dispatch, getState) => {
    try {
      const items = selectAllRecipeHeaderPrivateUsersData(getState(), recipeHeaderId);
      const item = items?.[0];
      if (!refresh && item) {
        return items;
      }

      if (!getState().recipes.isRecipeHeaderPrivateUsersLoading) {
        dispatch(recipesSlice.actions.setIsRecipeHeaderPrivateUsersLoading(true));
      }

      const { data } = await recipeHeaderPrivateUserService.getRecipeHeaderPrivateUsers(
        recipeHeaderId,
        viewAsCustomerId
      );

      if (data.IsSuccess) {
        dispatch(
          recipesSlice.actions.setManyRecipeHeaderPrivateUsers({
            data: data.ResultObject,
            recipeHeaderId: recipeHeaderId,
          })
        );
        return data.ResultObject;
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
        return [];
      }
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
      return [];
    } finally {
      dispatch(recipesSlice.actions.setIsRecipeHeaderPrivateUsersLoading(false));
    }
  };

export const updateRecipeHeaderPrivateUsers =
  (request: UpdateRecipeHeaderPrivateUsersRequest, resolution: Resolution, onSuccess?: () => void): AppThunk =>
  async (dispatch, getState) => {
    try {
      if (!getState().recipes.isRecipeHeaderPrivateUsersLoading) {
        dispatch(recipesSlice.actions.setIsRecipeHeaderPrivateUsersLoading(true));
      }

      const { data } = await recipeHeaderPrivateUserService.updateRecipeHeaderPrivateUsers(request);

      if (data.IsSuccess) {
        dispatch(getRecipeHeaderPrivateUsers(request.RecipeHeaderId, request.ViewAsCustomerId, true));
        onSuccess?.();
      } else {
        dispatch(recipesSlice.actions.setIsRecipeHeaderPrivateUsersLoading(false));
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (exception) {
      dispatch(recipesSlice.actions.setIsRecipeHeaderPrivateUsersLoading(false));
      appInsightsLogger.trackException({ exception });
    }
  };

// Recipe customer user manages
export const getRecipeCustomerUserManages =
  (customerId: string, refresh?: boolean): AppThunk =>
  async (dispatch, getState) => {
    try {
      const item = selectAllRecipeCustomerUserManages(getState().recipes.recipeCustomerUserManages)?.[0];
      if (!refresh && item?.CustomerId === customerId) {
        return;
      }

      if (!getState().recipes.isRecipeCustomerUserManagesLoading) {
        dispatch(recipesSlice.actions.setIsRecipeCustomerUserManagesLoading(true));
      }

      const { data } = await recipeCustomerUserManageService.getRecipeCustomerUserManages(customerId);

      if (data.IsSuccess) {
        dispatch(recipesSlice.actions.setRecipeCustomerUserManages(data.ResultObject));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }

      dispatch(recipesSlice.actions.setIsRecipeCustomerUserManagesLoading(false));
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
  };

export const updateRecipeCustomerUserManages =
  (request: UpdateRecipeCustomerUserManagesRequest, resolution: Resolution, onSuccess?: () => void): AppThunk =>
  async (dispatch, getState) => {
    try {
      if (!getState().recipes.isRecipeCustomerUserManagesLoading) {
        dispatch(recipesSlice.actions.setIsRecipeCustomerUserManagesLoading(true));
      }

      const { data } = await recipeCustomerUserManageService.updateRecipeCustomerUserManages(request);

      if (data.IsSuccess) {
        dispatch(
          logUserActivity({
            action: UserActivityAction.ManageSharedRecipeUsers,
            pageName: UserActivityPageName.ManageRecipeAccess,
            actionSummary: UserActivityActionSummary.ManageSharedRecipeUsers,
            resolution: resolution,
          })
        );
        dispatch(getRecipeCustomerUserManages(request.CustomerId, true));
        onSuccess?.();
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
        dispatch(recipesSlice.actions.setIsRecipeCustomerUserManagesLoading(false));
      }
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
      dispatch(recipesSlice.actions.setIsRecipeCustomerUserManagesLoading(false));
    }
  };

/*
 * Recipe Cost
 */
export const abortGetRecipeCost = (): AppThunk => async () => {
  recipeCostService.abortGetRecipeCost();
};
export const getRecipeCost =
  (request: GetRecipeCostRequest): AppThunk<Promise<RecipeCostHeader | undefined>> =>
  async (dispatch) => {
    let result;
    try {
      dispatch(recipesSlice.actions.setIsLoadingRecipeCost(true));
      const { data } = await recipeCostService.getRecipeCost(request);
      if (data.IsSuccess) {
        dispatch(recipesSlice.actions.setRecipeCost(data.ResultObject));
        dispatch(getRecipeCostUsdaProductStatusTypes());
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      dispatch(recipesSlice.actions.setIsLoadingRecipeCost(false));
      result = data?.ResultObject?.RecipeCostHeader;
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
    }
    return result;
  };

export const getRecipeSharedUsers =
  (viewAsCustomerId: string): AppThunk =>
  async (dispatch, getState) => {
    try {
      if (!getState().recipes.isLoadingRecipeUsers) {
        dispatch(recipesSlice.actions.setIsRecipeUsersLoading(true));
      }

      const { data } = await recipeUsersService.getRecipeSharedUsers(viewAsCustomerId);

      if (data.IsSuccess) {
        dispatch(recipesSlice.actions.setRecipeUsers(data.ResultObject));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }

      dispatch(recipesSlice.actions.setIsRecipeUsersLoading(false));
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
  };

export const getRecipePrivateUsers =
  (viewAsCustomerId: string, recipeHeaderId: string): AppThunk =>
  async (dispatch, getState) => {
    try {
      if (!getState().recipes.isLoadingRecipeUsers) {
        dispatch(recipesSlice.actions.setIsRecipeUsersLoading(true));
      }

      const { data } = await recipeUsersService.getRecipePrivateUsers(viewAsCustomerId, recipeHeaderId);

      if (data.IsSuccess) {
        dispatch(recipesSlice.actions.setRecipeUsers(data.ResultObject));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }

      dispatch(recipesSlice.actions.setIsRecipeUsersLoading(false));
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
  };

export const getRecipeMenuPrivateUsers =
  (viewAsCustomerId: string, recipeMenuHeaderId: string): AppThunk =>
  async (dispatch, getState) => {
    try {
      if (!getState().recipes.isLoadingRecipeUsers) {
        dispatch(recipesSlice.actions.setIsRecipeUsersLoading(true));
      }

      const { data } = await recipeUsersService.getRecipeMenuPrivateUsers(viewAsCustomerId, recipeMenuHeaderId);

      if (data.IsSuccess) {
        dispatch(recipesSlice.actions.setRecipeUsers(data.ResultObject));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }

      dispatch(recipesSlice.actions.setIsRecipeUsersLoading(false));
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
  };

export const getSubRecipeRecipes =
  (customerId: string, subRecipeHeaderId: string): AppThunk =>
  async (dispatch, getState) => {
    try {
      if (!getState().recipes.isSubRecipeRecipesLoading) {
        dispatch(recipesSlice.actions.setIsSubRecipeRecipesLoading(true));
      }

      const { data } = await recipeDetailService.getRecipeDetailHeadersBySubRecipe(customerId, subRecipeHeaderId);

      if (data.IsSuccess) {
        dispatch(recipesSlice.actions.setSubRecipeRecipes(data.ResultObject));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }

      dispatch(recipesSlice.actions.setIsSubRecipeRecipesLoading(false));
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
  };

/* Recipe Costing */

export const getRecipeCosting =
  (request: RecipeCostingRequest): AppThunk<Promise<FileResult | undefined>> =>
  async (dispatch: AppDispatch): Promise<FileResult | undefined> => {
    try {
      const { data } = await recipeReportService.getRecipeCosting(request);
      if (data.IsSuccess) {
        return data.ResultObject;
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
    }
  };

export const exportRecipeCosting =
  (request: RecipeCostingRequest): AppThunk =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (getState().inventoryManagement.isInventoryReportExporting) {
      return;
    }
    try {
      dispatch(recipesSlice.actions.setRecipeReportIsExporting(true));

      dispatch(
        upsertAppNotification(
          {
            Id: generateStaticId('toastNotification', ['recipe-report-toast']),
            NotificationType: NotificationType.Information,
            NotificationDisplayType: NotificationDisplayType.Toast,
            AutoDismiss: 10,
            CanUserDismiss: true,
            Key: NotificationKeys.Toast,
            Message: 'We are preparing your download.',
          },
          0
        )
      );

      const { data } = await recipeReportService.getRecipeCosting(request);
      if (data.IsSuccess) {
        openFileInCurrentWindow(data.ResultObject as FileResult);
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
    } finally {
      dispatch(recipesSlice.actions.setRecipeReportIsExporting(false));
    }
  };

export const getRecipeCostingUserOptions =
  (): AppThunk<Promise<RecipeCostingOptionDefaults | undefined>> =>
  async (): Promise<RecipeCostingOptionDefaults | undefined> => {
    try {
      const { data } = await recipeReportService.getRecipeCostingUserOptions();
      if (data.IsSuccess) {
        return data.ResultObject;
      }
    } catch (error: unknown) {
      appInsightsLogger.trackException({
        exception: error,
      });
    }

    return Promise.resolve(undefined);
  };
/* Recipe Menu Costing */

export const getRecipeMenuCosting =
  (request: RecipeMenuCostingRequest): AppThunk<Promise<FileResult | undefined>> =>
  async (dispatch: AppDispatch): Promise<FileResult | undefined> => {
    try {
      const { data } = await recipeReportService.getRecipeMenuCosting(request);
      if (data.IsSuccess) {
        return data.ResultObject;
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
    }
  };

export const exportRecipeMenuCosting =
  (request: RecipeMenuCostingRequest): AppThunk =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (getState().inventoryManagement.isInventoryReportExporting) {
      return;
    }
    try {
      dispatch(recipesSlice.actions.setRecipeReportIsExporting(true));

      dispatch(
        upsertAppNotification(
          {
            Id: generateStaticId('toastNotification', ['recipe-report-toast']),
            NotificationType: NotificationType.Information,
            NotificationDisplayType: NotificationDisplayType.Toast,
            AutoDismiss: 10,
            CanUserDismiss: true,
            Key: NotificationKeys.Toast,
            Message: 'We are preparing your download.',
          },
          0
        )
      );

      const { data } = await recipeReportService.getRecipeMenuCosting(request);
      if (data.IsSuccess) {
        openFileInCurrentWindow(data.ResultObject as FileResult);
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
    } finally {
      dispatch(recipesSlice.actions.setRecipeReportIsExporting(false));
    }
  };

export const getRecipeMenuCostingUserOptions =
  (): AppThunk<Promise<RecipeMenuCostingOptionDefaults | undefined>> =>
  async (): Promise<RecipeMenuCostingOptionDefaults | undefined> => {
    try {
      const { data } = await recipeReportService.getRecipeMenuCostingUserOptions();
      if (data.IsSuccess) {
        return data.ResultObject;
      }
    } catch (error: unknown) {
      appInsightsLogger.trackException({
        exception: error,
      });
    }

    return Promise.resolve(undefined);
  };

/* Recipe Costing */

export const getRecipeDetailReport =
  (request: RecipeDetailRequest): AppThunk<Promise<FileResult | undefined>> =>
  async (dispatch: AppDispatch): Promise<FileResult | undefined> => {
    try {
      const { data } = await recipeReportService.getRecipeDetailReport(request);
      if (data.IsSuccess) {
        return data.ResultObject;
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
    }
  };

export const exportRecipeDetailReport =
  (request: RecipeDetailRequest): AppThunk =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (getState().inventoryManagement.isInventoryReportExporting) {
      return;
    }
    try {
      dispatch(recipesSlice.actions.setRecipeReportIsExporting(true));

      dispatch(
        upsertAppNotification(
          {
            Id: generateStaticId('toastNotification', ['recipe-report-toast']),
            NotificationType: NotificationType.Information,
            NotificationDisplayType: NotificationDisplayType.Toast,
            AutoDismiss: 10,
            CanUserDismiss: true,
            Key: NotificationKeys.Toast,
            Message: 'We are preparing your download.',
          },
          0
        )
      );

      const { data } = await recipeReportService.getRecipeDetailReport(request);
      if (data.IsSuccess) {
        openFileInCurrentWindow(data.ResultObject as FileResult);
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
    } finally {
      dispatch(recipesSlice.actions.setRecipeReportIsExporting(false));
    }
  };

export const getRecipeDetailUserOptions =
  (): AppThunk<Promise<RecipeDetailOptionDefaults | undefined>> =>
  async (): Promise<RecipeDetailOptionDefaults | undefined> => {
    try {
      const { data } = await recipeReportService.getRecipeDetailUserOptions();
      if (data.IsSuccess) {
        return data.ResultObject;
      }
    } catch (error: unknown) {
      appInsightsLogger.trackException({
        exception: error,
      });
    }

    return Promise.resolve(undefined);
  };

export const sequenceRecipeDetails =
  (request: SequenceRecipeDetailsRequest, resolution: Resolution): AppThunk<Promise<boolean>> =>
  async (dispatch) => {
    let result = false;
    try {
      dispatch(recipesSlice.actions.setIsLoadingRecipeDetails(true));

      const { data } = await recipeDetailService.sequenceRecipeDetails(request);

      if (data.IsSuccess) {
        await dispatch(getRecipeDetails(request.RecipeHeaderId, request.CustomerId));
        dispatch(recipesSlice.actions.resetGridParameters());
        dispatch(
          logUserActivity({
            action: UserActivityAction.EditRecipe,
            pageName: UserActivityPageName.RecipeDetail,
            actionSummary: UserActivityActionSummary.EditRecipe,
            resolution: resolution,
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      result = data?.IsSuccess;
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
    dispatch(recipesSlice.actions.setIsLoadingRecipeDetails(false));

    return result;
  };

export const getRecipeCostUsdaProductStatusTypes =
  (): AppThunk => (dispatch: AppDispatch, getState: () => RootState) => {
    const productKeys = selectRecipeDetailUsdaProducts(getState().recipes.recipeCostDetails);
    const customerId = getState().customer.selectedCustomer?.CustomerId ?? '';
    dispatch(
      getUSDAProductStatusTypes(customerId, productKeys, (statusResults: USDAProductStatusTypeData[]) => {
        dispatch(recipesSlice.actions.setUsdaProductStatusType(statusResults));
      })
    );
  };
